import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import api from '../utils/api';

interface Document {
  id: number;
  title: string;
  description: string;
  version: string;
  is_current_version: boolean;
}

interface DocumentDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  onDocumentDeleted: () => void;
}

const DocumentDeleteDialog: React.FC<DocumentDeleteDialogProps> = ({
  open,
  onClose,
  document,
  onDocumentDeleted
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!document) return;

    try {
      setLoading(true);
      setError(null);

      await api.delete(`/documents/${document.id}/`);
      
      onDocumentDeleted();
      onClose();

    } catch (error: any) {
      console.error('Error deleting document:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(`Deactivation failed:\n${errorMessages}`);
        } else {
          setError(`Deactivation failed: ${errorData}`);
        }
      } else {
        setError('Failed to deactivate document. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
             <DialogTitle>
         <Box display="flex" alignItems="center">
           <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
           Deactivate Document
         </Box>
       </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

                 <Box sx={{ mb: 2 }}>
           <Typography variant="h6" gutterBottom>
             Are you sure you want to deactivate this document?
           </Typography>
          
                     <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
             <Box display="flex" alignItems="center" mb={1}>
               <WarningIcon sx={{ mr: 1, color: 'white' }} />
               <Typography variant="subtitle2" color="white">
                 Document will be hidden from active list
               </Typography>
             </Box>
             <Typography variant="body2" color="white">
               The document will be deactivated and hidden from the active document list. All audit records, acknowledgments, and assignment history will be preserved for compliance purposes.
             </Typography>
           </Box>

          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Document Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Title:</strong> {document.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Version:</strong> {document.version} {document.is_current_version && '(Current)'}
            </Typography>
            {document.description && (
              <Typography variant="body2" color="text.secondary">
                <strong>Description:</strong> {document.description}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
                 <Button 
           onClick={handleDelete} 
           variant="contained" 
           color="error"
           disabled={loading}
           startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
         >
           {loading ? 'Deactivating...' : 'Deactivate Document'}
         </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentDeleteDialog; 